import { LevelOfService } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineTempo } from '../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventDependency } from '../../../slices/marketplace/timeline/models/timeline-event-dependency.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';

export class TimelineEventSortingService {
    static placeUpdatedEvent(
        events: TimelineEvent[],
        updatedEvent: TimelineEvent
    ): TimelineEvent[] {
        if (!events || events.length == 0) return [updatedEvent];

        const prevEventState = events.find(
            (event) => event.id == updatedEvent.id
        );
        const prevEventIndex = prevEventState
            ? events.indexOf(prevEventState)
            : -1;

        const deps = new Set(
            updatedEvent.dependencies.map((dep) => dep.dependencyEventId)
        );
        events = events.filter((event) => event.id !== updatedEvent.id);

        const firstStageOccurrence =
            events
                .map((event, index) => ({ event, index }))
                .find(({ event }) => event.stage === updatedEvent.stage)
                ?.index ?? -1;

        const eventsFromStage = events.filter(
            (event) => event.stage === updatedEvent.stage
        );
        const eventsIdsFromStage = new Set(
            eventsFromStage.map((event) => event.id)
        );
        events = events.filter((event) => !eventsIdsFromStage.has(event.id));

        const lastPositionOfDependency =
            events
                .map((event, index) => ({ event, index }))
                .reverse()
                .find(({ event }) => deps.has(event.id))?.index ?? -1;
        const positionToInsertAfterDependency =
            lastPositionOfDependency == -1 ? -1 : lastPositionOfDependency + 1;

        let positionToPlace = Math.max(
            firstStageOccurrence,
            positionToInsertAfterDependency
        );
        positionToPlace =
            positionToPlace < 0 ? prevEventIndex : positionToPlace;
        positionToPlace = positionToPlace < 0 ? events.length : positionToPlace;

        events.splice(positionToPlace, 0, ...eventsFromStage, updatedEvent);
        return events;
    }

    static sortTimelineEvents(
        events: TimelineEvent[],
        levelOfService: LevelOfService,
        tempo?: TimelineTempo
    ): TimelineEvent[] {
        if (!events || events.length == 0) return [];

        const stagesOrder = this.getDistinctStages(events);
        const graph = this.mapDependenciesGraph(events);
        const sortedByDependencies = this.topologicalSort(
            events,
            graph
        ).reverse();

        const groupedByStagesEvents = sortedByDependencies.reduce(
            (groups, event) => {
                if (!groups[event.stage]) {
                    groups[event.stage] = [];
                }
                groups[event.stage].push({ ...event, dayStart: 0, dayEnd: 0 });
                return groups;
            },
            {} as Record<number, TimelineEvent[]>
        );

        const groupedSorted = Object.keys(groupedByStagesEvents)
            .sort(
                (a, b) =>
                    stagesOrder.indexOf(parseInt(a)) -
                    stagesOrder.indexOf(parseInt(b))
            )
            .flatMap((stage) => groupedByStagesEvents[parseInt(stage)]);

        this.fillStartEndDay(groupedSorted, tempo);

        const result = groupedSorted.sort((a, b) => {
            if (a.dayStart !== b.dayStart) return a.dayStart - b.dayStart;
            if (a.dayEnd !== b.dayEnd) return a.dayEnd - b.dayEnd;
            return a.name.localeCompare(b.name);
        });

        this.ensureDependentActionsOrderUnbroken(result);
        if (levelOfService == LevelOfService.StrategicFinancialAdvisoryService)
            this.ensureBuyerSelectionTeaserPostingAndExpressionOfInterestOrder(
                result
            );
        
        this.ensureMeetingEventsOrder(result);

        return result;
    }

    static ensureBuyerSelectionTeaserPostingAndExpressionOfInterestOrder(
        events: TimelineEvent[]
    ): void {
        const vvBuyerSelectionAndTeaserPostingEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
        );
        const sellerTeaserEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerTeaserApproval
        );
        const buyerExpressionOfInterestEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.BuyerExpressionOfInterest
        );

        if (!!vvBuyerSelectionAndTeaserPostingEvent) {
            if (
                !sellerTeaserEvent ||
                vvBuyerSelectionAndTeaserPostingEvent.dayEnd <
                    sellerTeaserEvent.dayEnd
            ) {
                throw new Error(
                    '"Seller Teaser Approval" must exist and its end date must be before "Buyer Selection / Teaser Posting" end date'
                );
            }
        }

        if (
            !!buyerExpressionOfInterestEvent &&
            (!sellerTeaserEvent ||
                buyerExpressionOfInterestEvent.dayStart <
                    sellerTeaserEvent.dayEnd)
        ) {
            throw new Error(
                '"Seller Teaser Approval" must exist and its end date must be before "Buyer Expression Of Interest" start date'
            );
        }
    }

    static ensureMeetingEventsOrder(events: TimelineEvent[]): void {
        const provideMeetingAvailabilityEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerProvideMeetingAvailability
        );
        const agreeOnMeetingTimesEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerAgreeOnMeetingTimes
        );
        const meetingsPeriodEvent = events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetingsPeriod
        );

        if (!!agreeOnMeetingTimesEvent) {
            if (!provideMeetingAvailabilityEvent) {
                throw new Error(
                    '"Provide Meeting Availability" must exist before "Agree on Meeting Times" can be added'
                );
            }
            const provideEndDate =
                provideMeetingAvailabilityEvent.dayCompleted ??
                provideMeetingAvailabilityEvent.dayEnd;
            const agreeEndDate =
                agreeOnMeetingTimesEvent.dayCompleted ??
                agreeOnMeetingTimesEvent.dayEnd;
            if (agreeEndDate <= provideEndDate) {
                throw new Error(
                    '"Agree on Meeting Times" end date must be later than "Provide Meeting Availability" end date'
                );
            }
        }

        if (!!meetingsPeriodEvent) {
            if (!provideMeetingAvailabilityEvent) {
                throw new Error(
                    '"Provide Meeting Availability" must exist before "Meetings Period" can be added'
                );
            }
            if (!agreeOnMeetingTimesEvent) {
                throw new Error(
                    '"Agree on Meeting Times" must exist before "Meetings Period" can be added'
                );
            }
            const provideEndDate =
                provideMeetingAvailabilityEvent.dayCompleted ??
                provideMeetingAvailabilityEvent.dayEnd;
            const agreeEndDate =
                agreeOnMeetingTimesEvent.dayCompleted ??
                agreeOnMeetingTimesEvent.dayEnd;
            const meetingsPeriodEndDate =
                meetingsPeriodEvent.dayCompleted ?? meetingsPeriodEvent.dayEnd;
            
            if (meetingsPeriodEndDate <= provideEndDate) {
                throw new Error(
                    '"Meetings Period" end date must be later than "Provide Meeting Availability" end date'
                );
            }
            if (meetingsPeriodEndDate <= agreeEndDate) {
                throw new Error(
                    '"Meetings Period" end date must be later than "Agree on Meeting Times" end date'
                );
            }
        }
    }

    static ensureDependentActionsOrderUnbroken(events: TimelineEvent[]): void {
        const eventsWithReferencedActions = events.filter(
            (event) => event.linkedAction?.actionReferenceId
        );
        if (eventsWithReferencedActions.length === 0) return;

        const isReferencesBefore = eventsWithReferencedActions.every(
            (event) => {
                const referencedAction = event.linkedAction?.actionReferenceId;
                if (referencedAction) {
                    const referencedEvent = events.find(
                        (e) => e?.linkedAction?.id === referencedAction
                    );
                    return (
                        referencedEvent?.linkedAction.status ===
                            TimelineActionStatus.Completed ||
                        referencedEvent?.dayEnd <= event.dayStart
                    );
                }
            }
        );
        if (!isReferencesBefore) {
            const invalidEvent = eventsWithReferencedActions.find((event) => {
                const referencedAction = event.linkedAction?.actionReferenceId;
                if (referencedAction) {
                    const referencedEvent = events.find(
                        (e) => e?.linkedAction?.id === referencedAction
                    );
                    return !(
                        referencedEvent?.linkedAction.status ===
                            TimelineActionStatus.Completed ||
                        referencedEvent?.dayEnd <= event.dayStart
                    );
                }
                return false;
            });
            const referencedAction =
                invalidEvent?.linkedAction?.actionReferenceId;
            const referencedEvent = events.find(
                (e) => e?.linkedAction?.id === referencedAction
            );
            throw new Error(
                `Invalid event order: "${invalidEvent?.name}" starts before its referenced action "${referencedEvent?.name}" ends.`
            );
        }
    }

    static getDistinctStages(events: TimelineEvent[]): number[] {
        const stages = events.map((event) => event.stage);
        return stages.filter((stage, index) => stages.indexOf(stage) === index);
    }

    static recalculateCompletedEarlierEvent = (
        currentEvents: TimelineEvent[],
        completedEvent: TimelineEvent,
        engagementStartDate: Date | string
    ) => {
        const currentEventsCopy = currentEvents.slice();
        const differenceMs =
            new Date().getTime() - new Date(engagementStartDate).getTime();
        const differenceDays: number = Math.floor(
            differenceMs / (1000 * 60 * 60 * 24)
        );

        const actualDayCompleted =
            (completedEvent.dayStart > differenceDays
                ? completedEvent.dayStart
                : differenceDays) + 1;
        const updatedEventIndex = currentEventsCopy.findIndex(
            (f) => f.id === completedEvent.id
        );
        const updatedCompletedEvent = {
            ...completedEvent,
            dayCompleted: actualDayCompleted,
        };
        currentEventsCopy[updatedEventIndex] = updatedCompletedEvent;

        const stageEventsExceptUpdated = currentEventsCopy.filter(
            (event) =>
                event.stage === completedEvent.stage &&
                event.id !== completedEvent.id
        );
        const isLastStageEventCompleted = stageEventsExceptUpdated.every(
            (event) => event.status === TimelineEventStatus.Completed
        );
        const dayOfLastCompletedEvent = stageEventsExceptUpdated.reduce(
            (max, event) => Math.max(max, event.dayCompleted ?? event.dayEnd),
            0
        );
        const shiftDaysForNextStagesCompletedEvents = isLastStageEventCompleted
            ? updatedCompletedEvent.dayEnd -
              Math.max(
                  dayOfLastCompletedEvent,
                  updatedCompletedEvent.dayCompleted
              )
            : 0;

        const shouldSkipDurationExtension = (event: TimelineEvent, completedEvent: TimelineEvent): boolean => {
            const eventDependsOnCompletedEvent = event.dependencies.some(dep => dep.dependencyEventId === completedEvent.id);
            
            const isInProgressInSameStage = 
                event.status === TimelineEventStatus.InProgress && 
                event.stage === completedEvent.stage;
            
            return !eventDependsOnCompletedEvent && isInProgressInSameStage;
        };

        const firstIncompleteEvent = currentEventsCopy.findIndex(
            (event, idx) =>
                idx !== updatedEventIndex &&
                event.status !== TimelineEventStatus.Completed &&
                !shouldSkipDurationExtension(event, updatedCompletedEvent)
        );
        const dayStartOfFirstIncompleteEvent =
            firstIncompleteEvent !== -1
                ? currentEventsCopy[firstIncompleteEvent].dayStart
                : undefined;

        const hasLaterInProgressEventsInSameStage = currentEventsCopy.some(
            (event) =>
                event.id !== completedEvent.id &&
                event.stage === completedEvent.stage &&
                event.status === TimelineEventStatus.InProgress &&
                event.dayEnd > updatedCompletedEvent.dayEnd &&
                !event.dependencies.some(dep => dep.dependencyEventId === completedEvent.id)
        );

        if (hasLaterInProgressEventsInSameStage) {
            const dependentEvents = currentEventsCopy.filter(event => 
                event.dependencies.some(dep => dep.dependencyEventId === completedEvent.id)
            );
            
            if (dependentEvents.length === 0) {
                return currentEventsCopy;
            }
        }

        const updatedRows = currentEventsCopy.map((event, idx) => {
            if (
                dayStartOfFirstIncompleteEvent !== undefined &&
                event.dayStart === dayStartOfFirstIncompleteEvent &&
                event.status !== TimelineEventStatus.Completed &&
                !shouldSkipDurationExtension(event, updatedCompletedEvent)
            ) {
                const hasDependencyOnCompletedEvent = event.dependencies.some(
                    dep => dep.dependencyEventId === completedEvent.id
                );
                const isInSameStage = event.stage === completedEvent.stage;
                
                let timeSavedByEarlyCompletion = 0;

                if (hasDependencyOnCompletedEvent && isInSameStage) {
                    timeSavedByEarlyCompletion = updatedCompletedEvent.dayEnd - updatedCompletedEvent.dayCompleted;
                } else if (!isInSameStage) {
                    const originalStageEvents = currentEventsCopy.filter(e => e.stage === completedEvent.stage);
                    const originalStageEndDate = Math.max(
                        ...originalStageEvents.map(e => e.dayEnd)
                    );

                    const newStageEvents = originalStageEvents.map(e => 
                        e.id === completedEvent.id ? updatedCompletedEvent : e
                    );
                    const newStageEndDate = Math.max(
                        ...newStageEvents.map(e => e.dayCompleted ?? e.dayEnd)
                    );

                    timeSavedByEarlyCompletion = originalStageEndDate - newStageEndDate;
                }
                
                if (timeSavedByEarlyCompletion > 0) {
                    return {
                        ...event,
                        slowTempoDurationDays:
                            event.slowTempoDurationDays +
                            timeSavedByEarlyCompletion,
                        mediumTempoDurationDays:
                            event.mediumTempoDurationDays +
                            timeSavedByEarlyCompletion,
                        fastTempoDurationDays:
                            event.fastTempoDurationDays +
                            timeSavedByEarlyCompletion,
                        currentTempoDurationDays:
                            event.currentTempoDurationDays +
                            timeSavedByEarlyCompletion,
                    };
                }
                return event;
            } else if (
                idx > updatedEventIndex &&
                shiftDaysForNextStagesCompletedEvents > 0 &&
                event.stage !== updatedCompletedEvent.stage &&
                event.status === TimelineEventStatus.Completed &&
                event.dayStart <= dayStartOfFirstIncompleteEvent
            ) {
                return {
                    ...event,
                    dayCompleted:
                        event.dayCompleted -
                        shiftDaysForNextStagesCompletedEvents,
                };
            }

            return event;
        });

        return updatedRows;
    };

    private static mapDependenciesGraph(
        events: TimelineEvent[]
    ): Record<string, string[]> {
        const graph: Record<string, string[]> = {};
        events.forEach((event) => {
            if (!graph[event.id]) graph[event.id] = [];

            event.dependencies.forEach(
                (dependency: TimelineEventDependency) => {
                    if (!graph[dependency.dependencyEventId])
                        graph[dependency.dependencyEventId] = [];
                    graph[dependency.dependencyEventId].push(event.id);
                }
            );
        });

        return graph;
    }

    private static fillStartEndDay(
        events: TimelineEvent[],
        tempo?: TimelineTempo
    ): void {
        let previousStageId = 0;
        let lastStageDayEnd = 0;
        let currentStageDayEnd = 0;

        for (let index = 0; index < events.length; index++) {
            const ev = events[index];
            if (previousStageId !== ev.stage) {
                previousStageId = ev.stage;
                lastStageDayEnd = currentStageDayEnd;
            }
            let maxDepDayEnd = 0;
            if (ev.dependencies && ev.dependencies.length > 0) {
                for (const dep of ev.dependencies) {
                    const depEvent = events.find(
                        (e) => e.id === dep.dependencyEventId
                    );
                    if (
                        !depEvent ||
                        depEvent.dayStart < 0 ||
                        depEvent.dayEnd <= 0
                    ) {
                        throw new Error('Events-Dependencies Order is Broken');
                    }
                    const depOffset = tempo
                        ? this.getDependencyOffsetBasedOnTempo(dep, tempo)
                        : dep.currentTempoOffsetDays;
                    const depDayEnd =
                        (depEvent.dayCompleted ?? depEvent.dayEnd) + depOffset;
                    if (depDayEnd > maxDepDayEnd) maxDepDayEnd = depDayEnd;
                }
            }

            const dayStart = Math.max(maxDepDayEnd, lastStageDayEnd);
            const eventDuration = tempo
                ? this.getEventDurationBasedOnTempo(ev, tempo)
                : ev.currentTempoDurationDays;
            const estimatedDayEnd = ev.dayCompleted ?? dayStart + eventDuration;
            currentStageDayEnd = Math.max(currentStageDayEnd, estimatedDayEnd);
            events[index] = { ...ev, dayStart, dayEnd: estimatedDayEnd };
        }
    }

    private static getDependencyOffsetBasedOnTempo(
        dependency: TimelineEventDependency,
        tempo: TimelineTempo
    ): number {
        switch (tempo) {
            case TimelineTempo.SLOW:
                return dependency.slowTempoOffsetDays;
            case TimelineTempo.MEDIUM:
                return dependency.mediumTempoOffsetDays;
            case TimelineTempo.FAST:
                return dependency.fastTempoOffsetDays;
        }
    }

    private static getEventDurationBasedOnTempo(
        event: TimelineEvent,
        tempo: TimelineTempo
    ): number {
        switch (tempo) {
            case TimelineTempo.SLOW:
                return event.slowTempoDurationDays;
            case TimelineTempo.MEDIUM:
                return event.mediumTempoDurationDays;
            case TimelineTempo.FAST:
                return event.fastTempoDurationDays;
        }
    }

    private static topologicalSort(
        events: TimelineEvent[],
        graph: Record<string, string[]>
    ): TimelineEvent[] {
        const sorted: string[] = [];
        const visited = new Set<string>();
        const visiting = new Set<string>();

        const dfs = (current: string): boolean => {
            if (visiting.has(current)) return false; // Cycle detected
            if (visited.has(current)) return true; // Already processed

            visiting.add(current);

            for (const neighbor of graph[current] || []) {
                if (!dfs(neighbor)) return false;
            }

            visiting.delete(current);
            visited.add(current);
            sorted.push(current);

            return true;
        };

        for (const event of events) {
            if (!visited.has(event.id)) {
                if (!dfs(event.id)) {
                    throw new Error(
                        'Invalid dependencies and dependent combination.'
                    );
                }
            }
        }

        return sorted.map(
            (id) => events.find((event) => event.id === id) as TimelineEvent
        );
    }

    private static ensureStatusesOrderUnbroken(events: TimelineEvent[]): void {
        for (let i = 0; i < events.length; i++) {
            const curr = events[i];

            if (curr.status !== TimelineEventStatus.Upcoming) {
                for (let j = 0; j < i; j++) {
                    const prev = events[j];

                    if (
                        prev.dayStart !== curr.dayStart &&
                        prev.status !== TimelineEventStatus.Completed
                    ) {
                        throw new Error(
                            `An uncompleted event cannot be placed before a Completed or In-Progress event.`
                        );
                    }
                }
            }
        }
    }
}
